import React, {CSSProperties, forwardRef, ReactElement, useCallback, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {connect} from "react-redux";
import './ContactSelect.less';
import * as ContactActions from "../../store/actions/contactActions";
import Select, {SelectFilterOptionFunc, SelectOnChangeFunc, SelectOnCreateOptionFunc} from "../../ui/Select/Select";
import Contact from "../../entities/contact";
import {parseFullName} from 'parse-full-name';
import util from "../../lib/util";
import createFilterOption, {contactComparator} from "../../ui/Select/CustomFilter";
import {deleteContact} from "@/store/deps/contactDeps";
import {formatLFM} from "../ContactFormat";
import {getCurrentFirmId, getCurrentUserId} from "@/lib/picos/currentLogin";
import {RootDispatch, RootState} from "@/@types/store";
import {uuid} from "@/@types/entity";
import type {SelectOption} from "@/@types/select";
import type {ActionMeta} from "react-select";
import useComponentCache from "../../hooks/useComponentCache";
import useStateWithCallback from "../../hooks/useStateWithCallback";

/**
 * All-in-one component to select existing contacts, or add new contacts.  Based on the Select/ReactSelect components.
 *
 * Includes an external method to add all new contacts to storage automatically. If you want to use that functionality,
 * you need to specify a ref callback in the tag:
 *
 *  <ContactSelect current={contactId | contactIds}
 *    isMulti={true}
 *    onChange={onParticipantChange}
 *  />
 *
 * For multi, assumes you only need the contactIds back.
 * For single, assumes you want the whole contact.
 */

type ContactSelectState = {
    added: Contact[];
    current: Contact | Contact[];
};

export interface ContactSelectHandle {
    saveNew: () => void;
    deleteNew: () => void;
    scrollIntoView: (b: boolean) => void;
}

const ContactSelect = forwardRef(function ContactSelect(props: ContactSelectProps, ref: React.ForwardedRef<ContactSelectHandle>) {
    const key = `ContactSelect_${props.name || ''}_${props.parentId || ''}`;

    const {cacheSave, cacheReset, cacheValue} = useComponentCache<ContactSelectState>(key || 'contact-select');

    const [added, setAdded] = useState<Contact[]>(cacheValue('added', []));

    const thisRef = useRef(null);

    /**
     * Special version required to handle contactsOverride.
     *
     * @param {uuid} id
     * @returns {Contact}
     */
    const getContactFromId = useCallback((id: uuid): Contact => {
        const contactsOverride = props.contactsOverrideFn ? props.contactsOverrideFn() : props.contactsOverride;
        return (contactsOverride || props.contacts)
            .concat(added)
            .find(c => c.id === id);
    }, [props, added]);

    const [current, setCurrent] = useStateWithCallback<Contact | Contact[]>(
        cacheValue('current',
            props.isMulti
            ? (props.current as string[])
                .map(cid => {
                    // props.current is a map of current *ids*
                    return getContactFromId(cid);
                }) ?? []
            : getContactFromId(props.current as string)));

    // ADD THIS EFFECT: Sync internal state when props.current changes
    useLayoutEffect(() => {
        if (props.isMulti) {
            const newCurrent = (props.current as string[])
                .map(cid => getContactFromId(cid))
                .filter(util.existsFilter);

            // Only update if the IDs actually changed
            const currentIds = (current as Contact[]).map(c => c?.id).sort().join(',');
            const newIds = newCurrent.map(c => c?.id).sort().join(',');

            if (currentIds !== newIds) {
                setCurrent(newCurrent);
            }
        }
        else {
            const newContact = getContactFromId(props.current as string);

            // Only update if the ID actually changed
            if ((current as Contact)?.id !== newContact?.id) {
                setCurrent(newContact);
            }
        }
    }, [props, getContactFromId, current, setCurrent]);

    const doResetRef = useRef<boolean>(false);

    const filterOption: SelectFilterOptionFunc = useMemo(() => createFilterOption(contactComparator), []);

    // Save state
    useLayoutEffect(() => {
        const reset = doResetRef.current;

        return () => {
            if (reset) cacheReset();
            else cacheSave({added, current});
        }
    }, [added, cacheReset, cacheSave, current]);

    useImperativeHandle(ref, () => ({
        saveNew: () => {
            doResetRef.current = true;
            added.forEach(c => {
                props.onSaveContactQueue(c);
            });
        },
        deleteNew: () => {
            doResetRef.current = true;
            added.forEach(c => {
                deleteContact(c);
            });
        },
        scrollIntoView: (b: boolean) => {
            thisRef.current.scrollIntoView(b);
        }
    }), [added, props]);

    // TODO: Pretty sure this is required!
    // static getDerivedStateFromProps(props, state) {
    //     /**
    //      * Extra-special version because this method is static and we need contactsOverride.
    //      *
    //      * @param {uuid} id
    //      * @returns {Contact}
    //      */
    //     const getContactFromId = (id) => {
    //         const contactsOverride = props.contactsOverrideFn ? props.contactsOverrideFn() : props.contactsOverride;
    //         return (contactsOverride || props.contacts)
    //             .concat(added)
    //             .find(c => c.id === id);
    //     };
    //
    //     if (props.isMulti) {
    //         return {
    //             current: props.current.map(cid => {
    //                 // props.current is a map of current *ids*
    //                 return getContactFromId(cid);
    //             })
    //         };
    //     }
    //     else {
    //         return {current: props.current};
    //     }
    // }

    /**
     * Ensure we are inputting a unique name here!  Ignore titles, but do NOT ignore suffixes.  Nicknames also not checked.
     * @param {string} [option]
     * @returns {boolean}
     */
    const isValidContactName = useCallback((option?: string) => {
        // No nulls
        if (!option) return false;

        // No blanks
        if (option.trim() === '') return false;

        const {first, middle, last, suffix} = parseFullName(option);

        return !(props.contactsOverride || props.contacts)
            .filter(c => c.status !== 'D')
            .concat(added)
            .some(c => {
                return c.first === (first || null) && c.last === (last || null) && c.middle === (middle || null) && c.suffix === (suffix || null);
            });
    }, [props.contactsOverride, props.contacts, added]);

    const onContactChange: SelectOnChangeFunc = (contactId: uuid | SelectOption<string, Contact>[], _actionMeta: ActionMeta<Contact[]>) => {
        const _added = [...added.filter(util.existsFilter)];

        if (!contactId) {
            if (props.onChange) props.onChange(props.isMulti ? [] : null, _added.map(c => c.id), _added);
        }
        else if (props.isMulti && Array.isArray(contactId)) {
            const contacts = contactId.map(c => c.data);

            // Check for deleted but new items
            _added.forEach(a => {
                // If not found, remove
                const found = contacts.find(c => c.id === a.id);

                if (!found) {
                    const index = _added.findIndex(aa => aa.id === a.id);
                    _added.splice(index, 1);
                }
            });

            setCurrent(contacts);

            if (props.onChange) props.onChange(
                contacts.filter(util.existsFilter).map(c => c.id), _added.map(a => a.id),
                _added
            );
        }
        else { // Single value
            const contact = props.contacts.get(contactId as uuid);
            setCurrent(contact);

            if (props.onChange) props.onChange(contactId as uuid, _added.map(c => c.id), _added);
        }
    };

    /**
     * Create a new contact (person or company)
     * @param {string} option - the contact's NAME only
     * @returns {SelectOption<string, Contact>}
     */
    const onCreateContact: SelectOnCreateOptionFunc = (option: string): SelectOption<string, Contact> => {
        const quotes = ['“', '‘', '"', "'", '’', '”'];

        const isCompany = quotes.includes(option[0]) && quotes.includes(option[option.length - 1]);

        const contact = new Contact({
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: util.newHRTimestamp(),
            status: 'C',
            entityType: 'contact'
        });

        if (isCompany) {
            contact.last = option.substring(1, option.length - 1);
            contact.isCompany = true;
        }
        else {
            const {title, first, middle, last, nick, suffix} = parseFullName(option.trim());
            contact.prefix = title;
            contact.first = first;
            contact.middle = middle;
            contact.last = last;
            contact.nick = nick;
            contact.suffix = suffix;
            contact.isCompany = false;
        }

        const label = formatLFM(contact, true);

        if (props.isMulti) {
            // If using autoSave, just save it. It will magically appear next time the props are read.
            // Otherwise, we should save it in state and tell the caller about the new entry
            const contacts = (current as Contact[]).concat([contact]);
            const _added = added.concat([contact]);

            setAdded(_added);
            setCurrent(contacts, () => {
                if (props.autoSave) props.onSaveContactQueue(contact);
                if (props.onChange) props.onChange(contacts.map(c => c.id), added.map(a => a.id), added);
            });
        }
        else {
            const _added = [...added, contact];

            setAdded(_added);
            setCurrent(contact, () => {
                if (props.autoSave) props.onSaveContactQueue(contact);
                if (props.onChange) props.onChange(contact.id, added.map(c => c.id), added);
            });
        }

        return {label, value: `${contact.id}`, data: contact};
    };

    const selectedOptions = useMemo(() => {
        if (props.isMulti) {
            return (current as Contact[])
                .map(c => {
                    if (!c) return null;
                    else return {label: formatLFM(c, true), value: c.id, data: c};
                }).filter(c => !!c);
        }

        if (current) return ({
            label: formatLFM(current as Contact, true),
            value: (current as Contact).id,
            data: current
        });

        return null;
    }, [current, props.isMulti]);

    // Add contacts from state that have been added only if they do not yet show up in contacts/contacts override
    const allContacts = (props.contactsOverride || props.contacts)
        .filter(util.deletedFilter);

    added
        .filter(c => !allContacts.map(c => c.id).includes(c.id))
        .forEach(c => allContacts.push(c));

    if (current) {
        if (props.isMulti) allContacts.concat(...current as Contact[]);
        else {
            const contact = current as Contact;
            if (contact && !allContacts.find(c => c.id === contact.id)) allContacts.push(contact);
        }
    }

    const allOptions: SelectOption<string, Contact>[] = props.options || util.distinctId(allContacts
        .filter(util.existsFilter))
        .map((contact) => {
            const contactName = contact.isCompany ? contact.last : formatLFM(contact, true);
            const label = contact.status === 'D'
                          ? <span className='u-deleted'>{contactName}</span>
                          : contactName;

            return {
                label,
                contactName: contactName,
                value: `${contact.id}`,
                data: contact
            };
        })
        .sort((a, b) => a.contactName.localeCompare(b.contactName));

    return (
        <div className='contact-select' ref={thisRef} style={props.styles}>
            <Select allowCreate={props.allowCreate ?? true} // Allow creation of new contacts
                    allowNone={true} // Allow an empty field
                    autoAdvance={props.autoAdvance}
                    autoFocus={props.autoFocus}
                    defaultValue={selectedOptions} // An array of {label, value, contact}
                    isMulti={props.isMulti} // Are multiple values allowed?
                    isSearchable={true}
                    isValidNewOption={isValidContactName} // Is the proposed new contact name valid?
                    name={props.name} // Name of the field
                    placeholder={props.placeholder ?? 'e.g. John Doe or "ACME Services"'} // Text displayed in empty field
                    onChange={onContactChange} // Called when a contact is selected (not called if added)
                    onCreateOption={onCreateContact} // Called when user asks to create a new contact
                    onFocus={props.onFocus} // Called when user asks to create a new contact
                    onMenuOpen={props.onMenuOpen} // Called when user asks to create a new contact
                    onInputChange={props.onInputChange}
                    optionType='Contact'
                    options={allOptions} // Array of all contacts in {label, value, contact} form
                    styles={props.styles} // Styles (in react-select object form) to apply
                    filterOption={filterOption}
                    disablePortaling={props.disablePortaling}
                    required={props.required}
            />
        </div>
    );
});

/**
 * Be careful, this component is not a PureComponent. If you add anything below, make sure to update the
 * shouldComponentUpdate function!
 */
export type ContactSelectProps = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps> &
    {
        // Allow creation of new contacts
        allowCreate?: boolean;

        // After a single-select value is picked, advance focus to the next form field
        autoAdvance?: boolean;

        // Should we autofocus?
        autoFocus?: boolean;

        // Should new contacts be saved automatically?
        autoSave?: boolean;

        // This will be a list of UUIDs in string form, representing the currently selected contacts
        current: string | string[];

        // Should we disable portaling?
        disablePortaling?: boolean;

        // Are multiple values allowed?
        isMulti?: boolean;

        // Unique key to use for state restoration with componentCache
        name: string;

        // Called when a contact is added or removed
        onChange: (clientIds: uuid | uuid[], addedIds: uuid[], added: Contact[]) => void;

        // Called when focused
        onFocus?: () => void;

        // Called when the dropdown opens
        onMenuOpen?: () => void;

        // When a change is made on the input
        onInputChange?: (input: string) => void;

        // Use your own list of options!
        options?: Array<{
            label: string | React.ReactElement;
            contactName: string;
            value: string;
            contact: Contact;
        }>;

        // Used in tandem with key to provide a unique key for this component in state
        parentId: string;

        // Text to use as a placeholder
        placeholder?: string | ReactElement;

        // Is this field required?
        required?: boolean;

        // Styles to apply to the internal Select instance
        styles?: CSSProperties;

        // Override props.contacts, useful if you are actively editing them!
        contactsOverride?: Contact[];

        // Callback function to get overridden contacts
        contactsOverrideFn?: () => Contact[];

        // Used in tandem with contactsOverrideFn. This is needed to trip refresh.
        unsavedEntity?: Contact | Contact[];

        onSaveContactQueue: (contact: Contact) => void;
    }

const mapStateToProps = (state: RootState) => {
    return {
        contacts: state.contacts
    };
};

const mapDispatchToProps = (dispatch: RootDispatch) => {
    return {
        onSaveContactQueue: (contact: Contact) => dispatch(ContactActions.saveContactQueue(contact)),
    };
};

const options = {forwardRef: true};

export default connect(mapStateToProps, mapDispatchToProps, null, options)(ContactSelect);
